import { getUserPermissions, isCurrentUserAdmin } from '@api/userAndStoreContext'

/**
 * i18n key rendered when the user's role cannot be confidently resolved to
 * one of the mapped roles below (unknown role string, failed fetch, or no
 * store selected yet). Deliberately distinct from getUserPermissions' own
 * '?? Associate' authorization default -- a display label is an assertion
 * of fact to the user, so an unresolved role must never silently render as
 * a specific (and possibly wrong) role. See LLD P519075237 §3.1 and §4e.
 */
export const GENERIC_ROLE_KEY = 'jsma-web-profile-roleGeneric'

/**
 * Gateway `Role` enum values (JwoStoreManagementGateway RoleMapper.java)
 * mapped to the i18n key that renders each role's display label in
 * Profile & Settings. `getUserPermissions().role` is a plain string, not
 * the enum type, so these are declared as string literals rather than an
 * imported enum to avoid a hard runtime dependency on the Gateway client
 * package for a display-only lookup.
 *
 * Roles with no mapped entry (FieldTechnicianManager, AmazonViewer,
 * AmazonAdmin) intentionally fall through to GENERIC_ROLE_KEY -- see LLD
 * §11 open question 2.
 */
export const ROLE_LABEL_KEYS: Record<string, string> = {
    Admin: 'jsma-web-profile-roleAdmin',
    StoreManager: 'jsma-web-profile-roleStoreManager',
    Associate: 'jsma-web-profile-roleAssociate',
    TechInstaller: 'jsma-web-profile-roleFieldTechnician',
}

/**
 * Resolves the i18n key for the current user's role label on the Profile &
 * Settings page.
 *
 * IMPORTANT: `isCurrentUserAdmin()` is checked FIRST. Admin is an
 * instance-level flag, not a per-store assignment, so an Admin user
 * typically has no store-scoped permissions row for the selected store and
 * `getUserPermissions(storeId).role` would fall back to its own
 * `'Associate'` authorization default. Checking admin status first is the
 * difference between fixing this ticket and re-introducing the exact class
 * of bug it describes for the Admin case. See LLD P519075237 §3.1
 * ("The Admin trap").
 *
 * @param storeId - the currently selected store, or undefined if no store
 *   has been selected yet (e.g. during initial app load).
 * @returns the i18n key to pass to `t(bundle, key)` for rendering.
 */
export async function resolveDisplayRole (storeId: string | undefined): Promise<string> {
    if (isCurrentUserAdmin()) {
        return ROLE_LABEL_KEYS.Admin
    }

    if (!storeId) {
        return GENERIC_ROLE_KEY
    }

    let permissions
    try {
        permissions = await getUserPermissions(storeId)
    } catch {
        // GetCurrentUser fetch failed or store context not yet hydrated.
        // Do NOT let this fall through to getUserPermissions' own
        // '?? Associate' authorization default -- that would render a
        // confident, wrong role, the same failure mode as this ticket.
        return GENERIC_ROLE_KEY
    }

    const role = permissions?.role
    if (!role || !(role in ROLE_LABEL_KEYS)) {
        return GENERIC_ROLE_KEY
    }

    return ROLE_LABEL_KEYS[role]
}
